import { RevxJobSize } from 'components/RevxJobSize';
import { Divider, Form, Tooltip } from 'antd';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMoney } from 'components/RevxMoney';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import {
  JobAdjustmentPrintingCategoryEnum,
  JobAdjustmentPrintingItemViewer,
  JobAdjustmentProcessStatusEnum,
  JobAdjustmentShippingItemViewer,
  JobAdjustmentViewer,
  JobItem,
  JobParticipantEnum,
  JobTypeEnum,
  ShipDateImpactEnum,
} from 'domains/workspaceDomain';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { formatNumberToThousandths } from 'utils/number';
import { useMount } from 'ahooks';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classnames from 'classnames';
import { JobconfigPrintingTipIcon } from 'components/Icons';
import { RevxDate } from 'components/RevxDate';
import RevxTextWithTooltip from 'components/RevxTextWithTooltip';
import { JobSpec, ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { OrientationEnum, SKU } from 'domains/skuDomain';
import { useOrg, useOrgById, useRole, useUser } from 'hooks';
import { useHistory } from 'react-router-dom';
import { addressTranslator } from 'utils/address';
import {
  JOB_ADJUSTMENT_WORKSPACE_FILTER,
  NOT_APPLICABLE,
  SHIPPING_WILL_CALL,
} from 'utils/constants';
import {
  getJobSpecPrintingBackStringForStep,
  getJobSpecPrintingFrontStringForStep,
} from 'utils/jobConfig';
import { HIGH_LIGHT_CLS, matchCategoryLabel } from './AdjustOriginal';
import {
  highlighterPrintingHandle,
  highlighterShippingHandle,
  highlighterTitleHandle,
} from './highlight';
import JobAdjustmentContact from './components/JobAdjustmentContact';
import JobAdjustmentOracleBillingCode, {
  getOracleBillingCodeRows,
} from './components/JobAdjustmentOracleBillingCode';
import { CHECKOUT_ALBERTSONS_ORGS } from 'utils/constants';

type Props = {
  data?: JobAdjustmentViewer;
  updateJobAdjustmentData: (data: any) => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  originalData?: JobAdjustmentViewer;
  jobData?: JobItem;
  goBackWorkspace?: () => void;
  onJobCancellation?: () => void;
};

type TotalRow = {
  label: string | undefined;
  original: boolean;
  key: TotalRowKeysEnum | string;
  description?: string;
  fee?: number;
  serialNumber?: number;
};

enum TotalRowKeysEnum {
  printing = 'printing',
  shipping = 'shipping',
  fee = 'fee',
  taxes = 'taxes',
  totalCharges = 'totalCharges',
}

enum JobCostTypeEnum {
  original = 'original',
  new = 'new',
}

const totalColumns = [
  {
    key: 'items',
    label: 'Items',
    width: 740,
  },
  {
    key: 'originalJobCost',
    label: 'Original Job Cost',
    width: 180,
  },
  {
    key: 'newJobCost',
    label: 'New Job Cost',
    width: 180,
  },
];

const totalRows: TotalRow[] = [
  {
    label: 'Printing',
    original: true,
    key: TotalRowKeysEnum.printing,
  },
  {
    label: 'Shipping',
    original: true,
    key: TotalRowKeysEnum.shipping,
  },
  {
    label: 'Taxes',
    original: false,
    key: TotalRowKeysEnum.taxes,
  },
  {
    label: 'Total charges',
    original: true,
    key: TotalRowKeysEnum.totalCharges,
  },
];

export const categoryColumns: {
  key: string;
  label: string;
  width?: number;
  dynamic: boolean;
  dynamicWidth?: number;
  flex?: number;
  dynamicFlex?: number;
}[] = [
  {
    key: 'category',
    label: 'Category',
    width: 256,
    flex: 5,
    dynamic: false,
  },
  {
    key: 'itemName',
    label: 'Name',
    width: 256,
    dynamicWidth: 600,
    flex: 5,
    dynamicFlex: 12,
    dynamic: false,
  },
  {
    key: 'totalCost',
    label: 'Total cost',
    width: 100,
    flex: 2,
    dynamic: false,
  },
];

const isHighCls = (is: boolean | undefined) => (is ? HIGH_LIGHT_CLS : '');

const JobAdjustOriginal = (props: Props) => {
  const { data, setLoading, jobData, goBackWorkspace, onJobCancellation } =
    props;
  const [adjustForm] = Form.useForm();
  // const { t } = useTranslation();
  const [categoryItem, setCategoryItem] =
    useState<JobAdjustmentPrintingItemViewer[]>();
  const [shippingItems, setShippingItems] =
    useState<JobAdjustmentShippingItemViewer[]>();
  const [feeLength, setFeeLength] = useState<number>(0);
  const [totalRowsState, setTotalRowsState] = useState<TotalRow[]>(totalRows);
  const [isCancel, setIsCancel] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const uiStatusFilter = useAppSelector(
    (state) => state.workspace.uiStatusFilter
  );
  const [adjustShippingPriceItem, setAdjustShippingPriceItem] =
    useState<JobAdjustmentPrintingItemViewer[]>();
  const [categoryListForm] = Form.useForm();
  const user = useUser();
  const org = useOrg();
  const { sourcerRole, supplierRole, buyerRole } = useRole();
  const canEditBillingContact =
    (sourcerRole && org?.sourcerAdjustBillingContact) ||
    (supplierRole && org?.supplierAdjustBillingContact);

  // Only Albertsons orgs split the charge over a cost distribution, everybody
  // else carries a single billing code. The job is billed to its customer org,
  // which is not always the org being viewed from.
  const isAlbertsonsOrg = !!(
    jobData?.customerOrgId &&
    CHECKOUT_ALBERTSONS_ORGS.includes(jobData.customerOrgId)
  );
  // The org the job was created in decides whether it works with a billing
  // code at all, so its row is kept even while empty when the org asked for it.
  const jobOrg = useOrgById(
    isAlbertsonsOrg ? undefined : jobData?.customerOrgId
  );

  const isInternalJob = jobData?.jobType === JobTypeEnum.INTERNAL;
  categoryListForm.setFieldsValue({ categoryItem: categoryItem });
  adjustForm.setFieldsValue({ categoryItem: adjustShippingPriceItem });
  const isOwnJob = user?.userId === data?.customerId;
  const isSupplierOwnJob = isOwnJob && supplierRole;
  const showCostCenterBySupplier =
    isSupplierOwnJob &&
    jobData?.participants.includes(JobParticipantEnum.PRODUCER) &&
    jobData?.participants.includes(JobParticipantEnum.PROXY);

  const highlighterShippingCls = (
    dataIndex: string,
    shipping: JobAdjustmentShippingItemViewer
  ) =>
    isHighCls(
      highlighterShippingHandle({ ...props, user, dataIndex, shipping })
    );

  useMount(() => {
    setShippingItems(data?.shippingItems);
    setFeeLength(1);
  });

  useEffect(() => {
    if (!data) return;
    const filterPrinting = data.printingItems.filter(
      (i) =>
        i.category !== JobAdjustmentPrintingCategoryEnum.SHIPPING &&
        !i.copyFromOldVersion
    );
    setCategoryItem(filterPrinting);
    initFeeRowData();
    setAdjustShippingPriceItem(
      data.printingItems.filter(
        (i) =>
          i.category === JobAdjustmentPrintingCategoryEnum.SHIPPING &&
          !i.copyFromOldVersion
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const initFeeRowData = () => {
    let initFeeLength = 0;
    if (data) {
      const newRow = totalRowsState.filter((row) => row.label);
      data.adjustTotal.fees.forEach(({ description, fee, serialNumber }) => {
        initFeeLength++;
        newRow.splice(findTotalFeeRowIndex(undefined, newRow), 0, {
          label: undefined,
          key: genFeeKey(initFeeLength),
          original: false,
          description,
          fee,
          serialNumber,
        });
      });
      setFeeLength(initFeeLength);
      setTotalRowsState(newRow);
    }
  };

  if (!data) return <></>;

  const {
    jobName,
    printing: {
      quantity,
      size,
      sizeMetric,
      sizePreset,
      sizePresetUnit,
      parts,
      orientation,
      services,
    },
    promoterRole,
  } = data;

  const isSourcerPage = promoterRole === SiteRoleEnum.Sourcer;

  const shippingTableColumn: TableColumnsType<JobAdjustmentShippingItemViewer> =
    [
      {
        title: 'Address',
        dataIndex: ['shippingInfo', 'toName'],
        render: (value, record, index) => {
          return (
            <RevxFlexLayout
              wrap="wrap"
              className={highlighterShippingCls('toName', record)}
            >
              <div style={{ width: '100%' }}>{value}</div>
              <div>{addressTranslator.dataToAddress(record.shippingInfo)}</div>
            </RevxFlexLayout>
          );
        },
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: () => {
          return (
            <div>
              <p>Ready date/</p>
              <p>Ship date</p>
            </div>
          );
        },
        width: 115,
        dataIndex: 'readyDate',
        render: (value, record, index) => {
          return {
            children: (
              <div className={highlighterShippingCls('readyDate', record)}>
                <RevxDate dateStr={value} />
              </div>
            ),
            props: {
              rowSpan: index === 0 ? shippingItems?.length : 0,
            },
          };
        },
      },
      {
        title: () => {
          return (
            <div>
              <p>Shipping</p>
              <p>account</p>
            </div>
          );
        },
        dataIndex: ['shippingInfo', 'accountType'],
        render: (value, record, index) => {
          const {
            shippingInfo: { accountCompany, carrierService },
          } = record;
          return (
            <div className={highlighterShippingCls('accountType', record)}>
              <div>
                {carrierService === SHIPPING_WILL_CALL
                  ? NOT_APPLICABLE
                  : accountCompany}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Shipping method',
        dataIndex: ['shippingInfo', 'carrierType'],
        render: (value, record, index) => {
          return (
            <div className={highlighterShippingCls('carrierService', record)}>
              <div>{record.shippingInfo.carrierService}</div>
            </div>
          );
        },
      },
      {
        title: 'Arrival date',
        dataIndex: ['deliveryDate'],
        width: 115,
        render: (value, record, index) => {
          return (
            <div className={highlighterShippingCls('deliveryDate', record)}>
              <RevxDate dateStr={value} />
            </div>
          );
        },
      },
      {
        title: () => {
          return (
            <div>
              <p>Requested</p>
              <p>delivery date</p>
            </div>
          );
        },
        width: 115,
        dataIndex: ['expectedDeliveryDate'],
        render: (value, record, index) => {
          return (
            <div
              className={highlighterShippingCls('expectedDeliveryDate', record)}
            >
              <RevxDate dateStr={value} />
            </div>
          );
        },
      },
      {
        title: 'Price',
        dataIndex: ['price'],
        width: 100,
        render: (value, record, index) => {
          const isSpecial =
            record?.shippingInfo?.carrierType === CarrierTypeEnum.UPS &&
            record?.shippingInfo?.accountType ===
              ShippingAccountTypeEnum.ORGANIZATION;
          return (
            <div className={highlighterShippingCls('price', record)}>
              {isSpecial ? (
                <>
                  <RevxMoney value={record.cost || 0} />
                  {` (billed separately)`}
                </>
              ) : (
                <>
                  <RevxMoney value={value || 0} />
                </>
              )}
            </div>
          );
        },
      },
    ];
  const findTotalFeeRowIndex = (
    otherFeeKey?: string,
    curTotalRows?: TotalRow[]
  ) => {
    let k = 0;
    (curTotalRows || totalRowsState).forEach(({ key }, index) => {
      if (otherFeeKey) {
        if (otherFeeKey === key) k = index;
      } else {
        if (key === TotalRowKeysEnum.fee) {
          k = index;
        }
      }
    });
    return k;
  };
  const genFeeKey = (len?: number) => {
    return TotalRowKeysEnum.fee + '_' + (len || feeLength);
  };

  const renderJobCostDom = (
    type: JobCostTypeEnum,
    key: TotalRowKeysEnum | string
  ) => {
    const { printing, shipping, fee, taxes, totalCharges } = TotalRowKeysEnum;
    const isOriginal = type === JobCostTypeEnum.original;
    switch (key) {
      case printing:
        if (isOriginal) {
          return <RevxMoney value={data.originalTotal.printingCost} />;
        }
        return (
          <div>
            <RevxMoney value={data.adjustTotal.printingCost} />
          </div>
        );
      case shipping:
        if (isOriginal) {
          return <RevxMoney value={data.originalTotal.shippingCost} />;
        }
        return (
          <div>
            <RevxMoney value={data.adjustTotal.shippingCost} />
          </div>
        );
      case fee:
        return <></>;
      case taxes:
        if (isOriginal) {
          return <RevxMoney value={data.originalTotal.tax} />;
        }
        return (
          <div>
            <RevxMoney value={data.adjustTotal.tax} />
          </div>
        );
      case totalCharges:
        if (isOriginal) {
          return <RevxMoney value={data.originalTotal.totalCharge} />;
        }
        return (
          <div>
            <RevxMoney value={data.adjustTotal.totalCharge} />
          </div>
        );
      default:
        const curFeeRow = totalRowsState[findTotalFeeRowIndex(key)];
        if (isOriginal) {
          return <></>;
        }
        return (
          <div>
            <RevxMoney value={curFeeRow?.fee || 0} />
          </div>
        );
    }
  };
  const renderTotalDom = (
    index: number,
    rowIndex: number,
    { label, key }: TotalRow
  ) => {
    switch (index) {
      case 0:
        const m = () => {
          if (label) {
            return label;
          } else {
            return (
              <RevxFlexLayout className="fee-wrap">
                <div className="fee-des">
                  <Tooltip
                    title={
                      totalRowsState[findTotalFeeRowIndex(key)].description
                    }
                  >
                    {totalRowsState[findTotalFeeRowIndex(key)].description}
                  </Tooltip>
                </div>
              </RevxFlexLayout>
            );
          }
        };
        return <div>{m()}</div>;
      case 1:
        return <div>{renderJobCostDom(JobCostTypeEnum.original, key)}</div>;
      case 2:
        return <div>{renderJobCostDom(JobCostTypeEnum.new, key)}</div>;
    }
  };

  const renderPrintingLayout = (isShipping: boolean) => {
    return (
      <RevxFlexLayout className="category-wrap" wrap="wrap">
        <RevxFlexLayout
          className={`category-list`}
          hidden={
            isShipping
              ? !adjustShippingPriceItem?.length
              : !categoryItem?.length
          }
        >
          {categoryColumns.map((i, index) => {
            const { key, label, dynamic, flex } = i;
            if (dynamic && sourcerRole) {
              return <></>;
            }
            const curLabelMap: { [key: string]: string } = {
              itemName: 'Item name',
              cost: 'Cost',
            };
            const reviewModelLabel = curLabelMap?.[key] || label;
            return (
              <div
                className="category-col"
                key={index}
                style={{
                  flex: sourcerRole ? i?.dynamicFlex || flex : flex,
                }}
              >
                <div className="category-col__title">
                  <span>{reviewModelLabel}</span>
                </div>
                <Form form={isShipping ? adjustForm : categoryListForm}>
                  <Form.List name="categoryItem">
                    {(fields) => {
                      return fields.map((field) => {
                        const items = isShipping
                          ? adjustShippingPriceItem
                          : categoryItem;
                        if (!items) {
                          return <div key={field.key}></div>;
                        }
                        const item = items[field.fieldKey];
                        if (item?.copyFromOldVersion) {
                          return null;
                        }
                        return (
                          <RevxFlexLayout
                            className="category-col__main"
                            key={item?.id}
                          >
                            {renderCategoryDom(index, item, field, isShipping)}
                          </RevxFlexLayout>
                        );
                      });
                    }}
                  </Form.List>
                </Form>
              </div>
            );
          })}
        </RevxFlexLayout>
      </RevxFlexLayout>
    );
  };

  const renderCategoryDom = (
    index: number,
    category: JobAdjustmentPrintingItemViewer,
    field: FormListFieldData,
    isShipping: boolean
  ) => {
    const defaultCategory = category;
    if (!defaultCategory) return <></>;
    const highlightClassName = highlighterPrintingHandle({
      ...props,
      index: index === 2 ? 4 : index,
      category,
      user,
    })
      ? HIGH_LIGHT_CLS
      : '';
    switch (index) {
      case 0:
        if (isShipping) {
          return (
            <div className={`td-category ${highlightClassName}`}>
              <div>Shipping</div>
            </div>
          );
        }
        return (
          <div className={`td-category ${highlightClassName}`}>
            <div>{matchCategoryLabel(defaultCategory?.category)}</div>
          </div>
        );
      case 1:
        return (
          <Form.Item
            {...field}
            name={[field.fieldKey, 'itemName']}
            fieldKey={[field.fieldKey, 'itemName']}
            className={highlightClassName}
          >
            <RevxTextWithTooltip
              text={defaultCategory?.itemName}
              maxWidth={400}
            />
          </Form.Item>
        );
      case 2:
        return (
          <div>
            {isSourcerPage ? (
              <div className={highlightClassName}>
                <RevxMoney value={defaultCategory?.totalCost || 0} />
              </div>
            ) : (
              <div
                style={{ fontWeight: 'bold' }}
                className={highlightClassName}
              >
                <RevxMoney value={defaultCategory?.totalCost || 0} />
              </div>
            )}
          </div>
        );
    }
  };
  const highlighterTitleCls = (dataIndex: string) =>
    isHighCls(highlighterTitleHandle({ ...props, user, dataIndex }));

  // Billing contact / cost center / accounting line stay hidden from a supplier
  // that is not also the proxy, the Oracle Billing Code is shown either way.
  const showBillingFields = !supplierRole || showCostCenterBySupplier;
  // Albertsons orgs carry the cost center and the accounting line inside the cost
  // distribution below, so the standalone fields would only repeat them.
  const showCostFields = !isAlbertsonsOrg;
  const hasBillingFields = canEditBillingContact || showCostFields;
  const oracleBillingCodeRows = isAlbertsonsOrg
    ? getOracleBillingCodeRows(
        data?.costDistribution,
        data?.adjustTotal?.totalCharge
      )
    : [];
  const showInlineOracleBillingCode =
    !!data?.oracleBillingCode ||
    !!jobOrg?.orderDetailPageConfig?.showOracleBillingCode;
  // This tab is a read only view of a submitted adjustment, so no edit pencil.
  // Without a code there is nothing to read and the row is dropped entirely.
  const oracleBillingCodeBlock =
    oracleBillingCodeRows.length > 0 ? (
      <JobAdjustmentOracleBillingCode
        rows={oracleBillingCodeRows}
        canShowEditButton={false}
        className={highlighterTitleCls('costDistribution')}
        onEdit={() => undefined}
      />
    ) : showInlineOracleBillingCode ? (
      <RevxFlexLayout
        className={`job-info__oracle ${highlighterTitleCls(
          'oracleBillingCode'
        )}`}
      >
        <span className="job-info__label">Oracle Billing Code:</span>
        <div className="job-info__oracle-value">
          <RevxTextWithTooltip text={data?.oracleBillingCode} maxWidth="100%" />
        </div>
      </RevxFlexLayout>
    ) : null;
  const hasHeaderContent =
    (showBillingFields && hasBillingFields) || !!oracleBillingCodeBlock;

  return (
    <>
      <div className="job-adjust-original-wrap">
        <div className="job-adjust-original">
          <div className="job-adjust-content">
            {hasHeaderContent && (
              <div
                className={`job-info job-info--multi-row${
                  showBillingFields ? '' : ' job-info--oracle-only'
                }`}
              >
                {showBillingFields && hasBillingFields && (
                  <RevxFlexLayout className="job-info__row">
                    {canEditBillingContact && (
                      <>
                        <RevxFlexLayout>
                          <span className="job-info__label">
                            Billing Contact:
                          </span>
                          <RevxFlexLayout
                            className={highlighterTitleCls('billingContact')}
                          >
                            <span>{data.billingContact}</span>
                          </RevxFlexLayout>
                        </RevxFlexLayout>
                        {showCostFields && <Divider type="vertical" />}
                      </>
                    )}
                    {showCostFields && (
                      <>
                        <RevxFlexLayout>
                          <span className="job-info__label">Cost Center:</span>
                          <RevxFlexLayout>
                            <span
                              className={
                                highlighterTitleHandle({
                                  ...props,
                                  dataIndex: 'costCenter',
                                  user,
                                })
                                  ? HIGH_LIGHT_CLS
                                  : ''
                              }
                            >
                              {data?.costCenter || 'None'}
                            </span>
                          </RevxFlexLayout>
                        </RevxFlexLayout>
                        <Divider type="vertical" />
                        <RevxFlexLayout>
                          <span className="job-info__label">
                            Accounting line:
                          </span>
                          <RevxFlexLayout>
                            <span
                              className={
                                highlighterTitleHandle({
                                  ...props,
                                  dataIndex: 'accountingLine',
                                  user,
                                })
                                  ? HIGH_LIGHT_CLS
                                  : ''
                              }
                            >
                              {data?.accountingLine || 'None'}
                            </span>
                          </RevxFlexLayout>
                        </RevxFlexLayout>
                      </>
                    )}
                  </RevxFlexLayout>
                )}
                {oracleBillingCodeBlock}
              </div>
            )}
            {!buyerRole && (
              <JobAdjustmentContact data={data} showCheckbox={false} />
            )}
            <RevxFlexLayout
              className="title-bar"
              justify="space-between"
              align="center"
            >
              <div className="title-bar__name">
                <span>Printing</span>
              </div>
              <div className="title-bar__price">
                <RevxMoney value={data.adjustTotal.printingCost} />
              </div>
            </RevxFlexLayout>
            <div
              className={classnames('base-info', {
                'is-single': !(parts.length > 1),
              })}
            >
              <div className="base-info__row bold normal">
                <span>{jobName}</span>
                <Divider type="vertical" />
                <span>QTY: </span>
                <span>{formatNumberToThousandths(quantity || 0)}</span>
              </div>
              {parts?.[0] && (
                <div className="base-info__row normal">
                  <span className="bold">Paper: </span>
                  <span>{parts[0].paperName}</span>
                </div>
              )}
              <div className="base-info__row normal">
                <span className="bold">Size: </span>
                <span>
                  <RevxJobSize
              isWorkspace
              size={size}
              sizeMetric={sizeMetric}
              sizePreset={sizePreset}
              sizePresetUnit={sizePresetUnit}
              orientation={orientation}
            />
                </span>
              </div>
              {parts?.[0] && (
                <div className="base-info__row normal">
                  <span className="bold">Press: </span>
                  <span>{parts[0].pressName}</span>
                  <Divider type="vertical" />
                  <span className="bold">Color: </span>
                  <span>
                    {parts[0].inkBack &&
                    parts[0].inkBack === parts[0].inkFront ? (
                      <>{`Both sides in color (${parts[0].inkBack})`}</>
                    ) : (
                      <>
                        {getJobSpecPrintingFrontStringForStep(
                          {
                            pmsFront: parts[0].pmsFront,
                            coatingFront: parts[0].coatingFront,
                            inkFront: parts[0].inkFront,
                          } as JobSpec,
                          {
                            name: parts[0].skuName,
                            skuType: {
                              name: parts[0].skuTypeName,
                            },
                          } as SKU
                        )}
                        <Divider type="vertical" />
                        {getJobSpecPrintingBackStringForStep(
                          {
                            pmsBack: parts[0].pmsBack,
                            coatingBack: parts[0].coatingBack,
                            inkBack: parts[0].inkBack,
                          } as JobSpec,
                          {
                            name: parts[0].skuName,
                            skuType: {
                              name: parts[0].skuTypeName,
                            },
                          } as SKU
                        )}
                      </>
                    )}
                  </span>
                </div>
              )}
              <div
                className="base-info__row normal"
                style={{
                  visibility:
                    orientation.toLocaleLowerCase() === OrientationEnum.cube
                      ? 'hidden'
                      : 'visible',
                }}
              >
                <span className="bold">Orientation: </span>
                <span style={{ textTransform: 'capitalize' }}>
                  {orientation}
                </span>
              </div>
              <div className="base-info__row normal">
                <span className="bold">Services: </span>
                {services.length > 0 ? (
                  <>
                    {' '}
                    {services.map((service, index) => (
                      <span key={service}>
                        <span>{service}</span>
                        {index !== services.length - 1 ? (
                          <Divider type="vertical" />
                        ) : (
                          <>{` `}</>
                        )}
                      </span>
                    ))}
                  </>
                ) : (
                  <span>PDF Proof - None</span>
                )}
              </div>
              {parts.length > 1 &&
                parts.slice(1).map((item, index) => {
                  return (
                    <div
                      key={item.paperName}
                      style={{
                        width: '100%',
                        display: 'flex',
                        flexWrap: 'wrap',
                        columnGap: '40px',
                      }}
                    >
                      <div
                        className="base-info__row title"
                        style={{ width: '100%' }}
                      >
                        {item.skuName}
                      </div>
                      <div className="base-info__row normal">
                        <span className="bold">Paper: </span>
                        <span>{item.paperName}</span>
                      </div>
                      <div className="base-info__row normal">
                        <span className="bold">Press: </span>
                        <span>{item.pressName}</span>
                        <Divider type="vertical" />
                        <span className="bold">Color: </span>
                        <span>
                          {item.inkBack && item.inkBack === item.inkFront ? (
                            <>{`Both sides in color (${item.inkBack})`}</>
                          ) : (
                            <>
                              {getJobSpecPrintingFrontStringForStep(
                                {
                                  pmsFront: item.pmsFront,
                                  coatingFront: item.coatingFront,
                                  inkFront: item.inkFront,
                                } as JobSpec,
                                {
                                  name: item.skuName,
                                  skuType: {
                                    name: item.skuTypeName,
                                  },
                                } as SKU
                              )}
                              <Divider type="vertical" />
                              {getJobSpecPrintingBackStringForStep(
                                {
                                  pmsBack: item.pmsBack,
                                  coatingBack: item.coatingBack,
                                  inkBack: item.inkBack,
                                } as JobSpec,
                                {
                                  name: item.skuName,
                                  skuType: {
                                    name: item.skuTypeName,
                                  },
                                } as SKU
                              )}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
            {renderPrintingLayout(false)}
            <RevxFlexLayout
              className="title-bar"
              justify="space-between"
              align="center"
            >
              <div className="title-bar__name">
                <span>Shipping</span>
              </div>
              <div className="title-bar__price">
                <RevxMoney value={data.adjustTotal.shippingCost} />
              </div>
            </RevxFlexLayout>
            <div className="shipping-block">
              {!!adjustShippingPriceItem?.length && renderPrintingLayout(true)}
              <RevxFlexLayout className="shipping-impact">
                {data.shipDateImpact === ShipDateImpactEnum.OTHER && (
                  <div className="shipping-impact__reason">
                    <div>
                      Impact to the ship date: {data.shipDateImpactAttachment}
                    </div>
                  </div>
                )}
              </RevxFlexLayout>
              <div className={`shipping-table`}>
                <RevxTable<JobAdjustmentShippingItemViewer>
                  dataSource={shippingItems}
                  columns={shippingTableColumn}
                  pagination={false}
                  rowKey="serialNumber"
                />
              </div>
            </div>
            <RevxFlexLayout
              className="title-bar"
              justify="space-between"
              align="center"
            >
              <div className="title-bar__name">
                <span>Total</span>
              </div>
            </RevxFlexLayout>
            <RevxFlexLayout className="total-block">
              <RevxFlexLayout className="total-list">
                {totalColumns.map(({ label, width, key }, index) => {
                  return (
                    <div
                      className={classnames('total-col', {
                        'first-col': index === 0,
                      })}
                      key={index}
                      style={{ width: index === 0 ? 740 : width }}
                    >
                      <RevxFlexLayout className="total-col__title">
                        <span>{label}</span>
                      </RevxFlexLayout>
                      {totalRowsState.map((rowItem, totalRowIndex) => {
                        const { key } = rowItem;
                        if (
                          key === TotalRowKeysEnum.taxes &&
                          (isInternalJob ||
                            jobData?.participants.includes(
                              JobParticipantEnum.PRODUCER
                            ))
                        ) {
                          return <></>;
                        }

                        return (
                          <RevxFlexLayout
                            className={classnames('total-col__main', {
                              'fee-td': key === TotalRowKeysEnum.fee,
                              'total-td': key === TotalRowKeysEnum.totalCharges,
                            })}
                            key={key}
                          >
                            {renderTotalDom(index, totalRowIndex, rowItem)}
                          </RevxFlexLayout>
                        );
                      })}
                    </div>
                  );
                })}
              </RevxFlexLayout>
            </RevxFlexLayout>
          </div>
          <div className="job-adjust-footer">
            <RevxFlexLayout
              className="original action-btn"
              justify="justify-end"
              hidden={
                isCancel ||
                [JobAdjustmentProcessStatusEnum.ACCEPTED].includes(
                  data.processStatus
                )
              }
            >
              <RevxButton
                onClick={() => {
                  // history.goBack();
                  setIsCancel(true);
                }}
              >
                Cancel Job
              </RevxButton>
              <RevxButton
                type="ghost"
                onClick={async () => {
                  try {
                    setLoading(true);
                    dispatch.app.showLoading();
                    await dispatch.rfm.acceptJobAdjustments({
                      jobId: data.jobId,
                      id: data.jobAdjustmentId,
                    });
                    setLoading(false);
                    dispatch.app.hideLoading();
                    // 如果有 goBackWorkspace 回调（在弹窗中），则调用它关闭弹窗
                    if (goBackWorkspace) {
                      goBackWorkspace();
                    } else {
                      // 如果没有回调（在页面中），则导航到 workspace
                      history.replace(
                        `/workspace/${
                          localStorage.getItem(
                            JOB_ADJUSTMENT_WORKSPACE_FILTER
                          ) || uiStatusFilter
                        }`
                      );
                    }
                  } finally {
                    dispatch.app.hideLoading();
                    setLoading(false);
                  }
                }}
              >
                Accept Adjustment
              </RevxButton>
            </RevxFlexLayout>
            <RevxFlexLayout
              className="job-adjustment-cancel"
              direction="column"
              hidden={!isCancel}
            >
              <RevxFlexLayout className="disc-text-item">
                <JobconfigPrintingTipIcon />
                Note that by canceling the job, you may incur charges and fees
                for any work completed to date or paper restocking.
              </RevxFlexLayout>

              <RevxFlexLayout>
                <RevxButton
                  onClick={() => {
                    // history.goBack();
                    setIsCancel(false);
                  }}
                >
                  No, don’t cancel
                </RevxButton>
                <RevxButton
                  type="primary"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      dispatch.app.showLoading();
                      await dispatch.rfm.createJobCancellations(
                        String(data.jobId)
                      );
                      setLoading(false);
                      dispatch.app.hideLoading();
                      // 如果有 onJobCancellation 回调（在弹窗中），则调用它重新加载数据
                      if (onJobCancellation) {
                        onJobCancellation();
                      } else if (goBackWorkspace) {
                        // 如果没有 onJobCancellation 但有 goBackWorkspace，则关闭弹窗
                        goBackWorkspace();
                      } else {
                        // 如果都没有（在页面中），则导航到 order-history
                        history.replace('/order-history');
                      }
                    } finally {
                      dispatch.app.hideLoading();
                      setLoading(false);
                    }
                  }}
                >
                  Yes, cancel job
                </RevxButton>
              </RevxFlexLayout>
            </RevxFlexLayout>
          </div>
        </div>
      </div>
    </>
  );
};
export default JobAdjustOriginal;
